import { Component, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { MetricsService } from '../../shared/metrics.service';
import { MetricQuery, MetricResult, MetricScope } from '../../interfaces/metrics';

function scopeSig(s: MetricScope): string {
  switch (s.type) {
    case 'self': return 'self|';
    case 'department': return `department|${s.id}`;
    case 'user': return `user|${s.id}`;
    case 'company': return 'company|';
    default: { const _exhaustive: never = s; return String(_exhaustive); }
  }
}

export type WidgetView =
  | { kind: 'loading' } | { kind: 'data'; result: MetricResult }
  | { kind: 'empty' } | { kind: 'error' } | { kind: 'locked' };

@Component({ selector: 'bst-widget-host', templateUrl: './widget-host.component.html', styleUrls: ['./widget-host.component.scss'], standalone: false })
export class WidgetHostComponent implements OnInit, OnChanges {
  // `title` doubles as a native HTML global attribute, so `<bst-widget-host title="dashboard.team.summary">`
  // also sets a native `title=` on the host and the raw i18n key leaks as a browser tooltip. Null it out.
  @HostBinding('attr.title') readonly hostTitle: string | null = null;

  @Input() query!: MetricQuery;
  @Input() title = '';
  @Input() subtitle = '';
  @Input() emptyTitleKey = 'dashboard.state.empty_title';
  @Input() emptySubtitleKey = 'dashboard.state.empty_sub';
  @Input() emptyActionKey = '';          // e.g. 'dashboard.empty.log_hours'
  @Input() drillable = false;
  // Fire the query but render no visible card. Used when a consumer only needs
  // this host's `last` result (a cross-referenced scalar), not its own card.
  @Input() bare = false;
  // Keep the loading/error gate + projected content, but drop the card chrome
  // (no bg/border/shadow/title) so the projected children are the top-level
  // cards. Avoids a card-inside-a-card when a host composes several cards.
  @Input() flush = false;
  @Output() drilldown = new EventEmitter<MetricResult>();
  @Output() emptyAction = new EventEmitter<void>();

  private reload$ = new Subject<void>();
  private initialized = false;
  private querySig = '';
  view$!: Observable<WidgetView>;
  last?: MetricResult;

  constructor(private metrics: MetricsService) {}

  ngOnInit(): void {
    this.querySig = this.sig();
    this.view$ = this.reload$.pipe(
      startWith(void 0),
      switchMap(() => this.metrics.get(this.query).pipe(
        map((r): WidgetView => {
          this.last = r;
          if (r.state === 'no_permission') return { kind: 'locked' };
          if (r.state === 'empty') return { kind: 'empty' };
          if (r.state !== 'ok') { throw new Error('unexpected metric state: ' + (r as any).state); }
          return { kind: 'data', result: r };
        }),
        startWith<WidgetView>({ kind: 'loading' }),
        catchError(err => {
          console.error('metrics widget load failed', { key: this.query?.key, err });
          return of<WidgetView>({ kind: 'error' });
        }),
      )),
    );
    this.initialized = true;
  }

  // [query]="q(...)" yields a fresh object every change-detection cycle, so
  // guard on a content signature: re-query only when key/scope/period/anchor
  // actually change (e.g. the period selector moved or the department picker
  // changed), never on identical-content churn.
  ngOnChanges(): void {
    if (!this.initialized) { return; }   // first bind is handled by ngOnInit's startWith
    const sig = this.sig();
    if (sig === this.querySig) { return; }
    this.querySig = sig;
    this.reload$.next();
  }

  private sig(): string {
    return this.query ? `${this.query.key}|${scopeSig(this.query.scope)}|${this.query.period.type}|${this.query.period.anchor}|${this.query.horizonWeeks ?? ''}` : '';
  }

  retry(): void { this.reload$.next(); }
  onCardClick(v: WidgetView): void { if (!this.drillable) return; if (v.kind === 'data') this.drilldown.emit(this.last); }
}
